import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  NotificationIcon,
  ShootingStarIcon,
  TopUpIcon,
} from '../../../assets/icons/icons';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { refreshCards, useCarouselCards } from '../../../assets/hooks/useCardData';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import {
  getSelectedCardId,
  isCardIssueFundingCovered,
  isCardFrozen,
  isNotIssuedCardStatus,
} from '../../../assets/services/cards';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import { getUserNotificationsCount } from '../../../assets/services/notifications';
import { getHistoryCardOperationFilters } from '../../../assets/constants/historyData';
import {
  fetchTransactionsPage,
  getTransactionRouteSnapshot,
} from '../../../assets/services/transactions';
import {
  cancelDepositFlow,
  setDepositCard,
  setDepositReturnPath,
} from '../../../assets/services/deposit';
import { isUnlockSessionReady } from '../../../assets/services/unlockSession';
import TelegramUserAvatar from '../../shared/TelegramUserAvatar/TelegramUserAvatar';
import TxCard from '../../shared/TxCard/TxCard';
import {
  DEFAULT_HORIZONTAL_PANEL_DIRECTION,
  getIndexedHorizontalPanelDirection,
  HORIZONTAL_PANEL_TRANSITION,
  HORIZONTAL_PANEL_VARIANTS,
} from '../../shared/motion/horizontalPanelMotion';
import HomeCardCarousel from '../HomeCardCarousel/HomeCardCarousel';
import './Home.css';

const HOME_CARDS_POLL_INTERVAL_MS = 15000;
const HOME_TRANSACTIONS_POLL_INTERVAL_MS = 15000;
const HOME_USER_POLL_INTERVAL_MS = 20000;
const HOME_USER_POLL_START_DELAY_MS = 2500;

function getHomeDisplayName(currentUser, telegramUser) {
  const personalData = currentUser?.personal_data || {};
  const displayName = [
    telegramUser?.first_name,
    telegramUser?.username ? `@${telegramUser.username}` : '',
    personalData.name,
    telegramUser?.id ? String(telegramUser.id) : '',
    currentUser?._id ? String(currentUser._id) : '',
  ].find((value) => typeof value === 'string' && value.trim());

  return displayName?.trim() || '';
}

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const { user } = useTelegram();
  const { currentUser, refreshCurrentUser } = useContext(CurrentUserContext);
  const { card, home, navigation } = useContext(TranslationContext);
  const displayName = getHomeDisplayName(currentUser, user);
  const carouselCards = useCarouselCards();
  const recentTransactionsRequestRef = useRef(0);
  const [homeCardView, setHomeCardView] = useState(() => ({
    selectedId: getSelectedCardId(),
    contentDirection: DEFAULT_HORIZONTAL_PANEL_DIRECTION,
  }));
  const {
    selectedId: selectedHomeCardId,
    contentDirection: homeCardContentDirection,
  } = homeCardView;
  const activeHomeCardId = useMemo(() => {
    if (selectedHomeCardId === null) {
      return '';
    }

    const selectedCard = carouselCards.find((item) => item.id === selectedHomeCardId);

    return selectedCard?.id || carouselCards[0]?.id || '';
  }, [carouselCards, selectedHomeCardId]);
  const activeHomeCard = carouselCards.find((item) => item.id === activeHomeCardId) || null;
  const isActiveHomeCardNotIssued = isNotIssuedCardStatus(activeHomeCard?.status);
  const isActiveHomeCardIssueFundingCovered = Boolean(
    isActiveHomeCardNotIssued && isCardIssueFundingCovered(activeHomeCard)
  );
  const shouldTopUpToActivate = Boolean(
    isActiveHomeCardNotIssued && !isActiveHomeCardIssueFundingCovered
  );
  const canTopUpActiveHomeCard = Boolean(
    activeHomeCard?.status === 'active' && !isCardFrozen(activeHomeCardId)
  );
  const shouldShowHomeCardAction = shouldTopUpToActivate || canTopUpActiveHomeCard;
  const [recentActivity, setRecentActivity] = useState({
    cardId: '',
    items: [],
    isLoading: false,
    isLoaded: false,
  });
  const recentTransactions = recentActivity.cardId === activeHomeCardId
    ? recentActivity.items
    : [];
  const isTransactionsInitialLoading = Boolean(
    activeHomeCardId
    && (recentActivity.cardId !== activeHomeCardId || recentActivity.isLoading)
  );
  const unreadCount = getUserNotificationsCount(currentUser, 0);
  const unreadBadge = unreadCount > 99 ? '99+' : String(unreadCount);
  const canPollSessionData = isUnlockSessionReady();

  const handleTopUpClick = () => {
    if (!activeHomeCardId) {
      return;
    }

    if (shouldTopUpToActivate) {
      navigate(`/cards/new?continueIssue=1&hideBack=1&cardId=${encodeURIComponent(activeHomeCardId)}`, {
        state: {
          fromPendingCard: true,
          hideBack: true,
          cardId: activeHomeCardId,
        },
      });
      return;
    }

    if (!canTopUpActiveHomeCard) {
      return;
    }

    const returnPath = `${location.pathname}${location.search}${location.hash}`;

    cancelDepositFlow();
    setDepositCard(activeHomeCardId);
    setDepositReturnPath(returnPath);
    navigate('/deposit', {
      state: { returnPath },
    });
  };
  const openNotifications = () => {
    triggerImpactHaptic('light');
    navigate('/notifications', {
      state: {
        activeTab: 'home',
        backPath: '/home',
      },
    });
  };
  const openProfile = () => {
    triggerImpactHaptic('light');
    navigate('/profile');
  };
  const handleSelectedHomeCardChange = (nextSelectedId) => {
    setHomeCardView((current) => {
      if (current.selectedId === nextSelectedId) {
        return current;
      }

      const currentIndex = current.selectedId === null
        ? carouselCards.length
        : Math.max(0, carouselCards.findIndex((item) => item.id === current.selectedId));
      const nextIndex = nextSelectedId === null
        ? carouselCards.length
        : Math.max(0, carouselCards.findIndex((item) => item.id === nextSelectedId));

      return {
        selectedId: nextSelectedId,
        contentDirection: getIndexedHorizontalPanelDirection(currentIndex, nextIndex),
      };
    });
  };

  useEffect(() => {
    if (!canPollSessionData) {
      return undefined;
    }

    let isCardsRequestRunning = false;

    const pollCards = async () => {
      if (document.hidden || isCardsRequestRunning) {
        return;
      }

      isCardsRequestRunning = true;

      try {
        await refreshCards({ limit: 25, silent: true });
      } catch {
        // Silent refresh keeps the current visible card state.
      } finally {
        isCardsRequestRunning = false;
      }
    };

    pollCards();
    const timer = window.setInterval(pollCards, HOME_CARDS_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [canPollSessionData]);

  useEffect(() => {
    if (!canPollSessionData) {
      return undefined;
    }

    let isUserRequestRunning = false;
    let timer = null;

    const pollUser = async () => {
      if (document.hidden || isUserRequestRunning) {
        return;
      }

      isUserRequestRunning = true;

      try {
        await refreshCurrentUser({ silent: true });
      } catch {
        // Silent refresh should not interrupt the home screen.
      } finally {
        isUserRequestRunning = false;
      }
    };

    const startTimer = window.setTimeout(() => {
      pollUser();
      timer = window.setInterval(pollUser, HOME_USER_POLL_INTERVAL_MS);
    }, HOME_USER_POLL_START_DELAY_MS);

    return () => {
      window.clearTimeout(startTimer);

      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [canPollSessionData, refreshCurrentUser]);

  useEffect(() => {
    if (!canPollSessionData) {
      return undefined;
    }

    if (!activeHomeCardId) {
      setRecentActivity({
        cardId: '',
        items: [],
        isLoading: false,
        isLoaded: true,
      });
      return undefined;
    }

    const requestScope = recentTransactionsRequestRef.current + 1;
    let isActive = true;
    let isTransactionsRequestRunning = false;

    recentTransactionsRequestRef.current = requestScope;
    setRecentActivity({
      cardId: activeHomeCardId,
      items: [],
      isLoading: true,
      isLoaded: false,
    });

    const pollTransactions = async () => {
      if (document.hidden || isTransactionsRequestRunning) {
        return;
      }

      isTransactionsRequestRunning = true;

      try {
        const page = await fetchTransactionsPage({
          limit: 4,
          filters: getHistoryCardOperationFilters(activeHomeCardId),
        });

        if (!isActive || recentTransactionsRequestRef.current !== requestScope) {
          return;
        }

        setRecentActivity({
          cardId: activeHomeCardId,
          items: page.items.slice(0, 4),
          isLoading: false,
          isLoaded: true,
        });
      } catch {
        if (isActive && recentTransactionsRequestRef.current === requestScope) {
          setRecentActivity((current) => (
            current.cardId === activeHomeCardId
              ? {
                ...current,
                isLoading: false,
                isLoaded: true,
              }
              : current
          ));
        }
      } finally {
        isTransactionsRequestRunning = false;
      }
    };

    pollTransactions();
    const timer = window.setInterval(
      pollTransactions,
      HOME_TRANSACTIONS_POLL_INTERVAL_MS
    );

    return () => {
      isActive = false;
      window.clearInterval(timer);
    };
  }, [activeHomeCardId, canPollSessionData]);

  return (
    <section className="home">
      <div className="home__content">
        <header className="home__header">
          <div className="home__header-text">
            <p className="home__greeting">{home.welcomeBack}</p>
            <h1 className="home__name">{displayName}</h1>
          </div>

          <div className="home__header-actions">
            <button
              type="button"
              className="home__notifications"
              aria-label={home.notifications}
              onClick={openNotifications}
            >
              <NotificationIcon className="home__notifications-icon" />
              {unreadCount > 0 && (
                <span
                  className="home__notifications-badge"
                  aria-label={`${unreadCount} unread notifications`}
                >
                  {unreadBadge}
                </span>
              )}
            </button>
            <button
              type="button"
              className="home__profile"
              aria-label={home.profile}
              onClick={openProfile}
            >
              <TelegramUserAvatar className="home__profile-avatar" />
            </button>
          </div>
        </header>

        <HomeCardCarousel
          cards={carouselCards}
          selectedId={activeHomeCardId}
          onSelectedChange={handleSelectedHomeCardChange}
        />

        <div className="home__card-content-viewport">
          <AnimatePresence
            initial={false}
            custom={homeCardContentDirection}
          >
            <motion.div
              key={activeHomeCardId || 'add-card'}
              className="home__card-content"
              custom={homeCardContentDirection}
              variants={HORIZONTAL_PANEL_VARIANTS}
              initial={shouldReduceMotion ? false : 'enter'}
              animate="center"
              exit={shouldReduceMotion ? undefined : 'exit'}
              transition={shouldReduceMotion
                ? { duration: 0 }
                : HORIZONTAL_PANEL_TRANSITION}
            >
              {shouldShowHomeCardAction && (
                <button
                  type="button"
                  className={`home__deposit${shouldTopUpToActivate ? ' home__deposit--continue-issue' : ''}`}
                  onClick={handleTopUpClick}
                >
                  {!shouldTopUpToActivate && <TopUpIcon className="home__deposit-icon" />}
                  {shouldTopUpToActivate ? card.topUpToActivate : navigation.topUp}
                </button>
              )}

              {activeHomeCardId ? (
                <section className="home__activity">
                  <div className="home__activity-header">
                    <h2 className="home__activity-title">{home.recentActivity}</h2>
                    <button
                      type="button"
                      className="home__activity-link"
                      onClick={() => navigate('/history')}
                    >
                      {home.seeAll}
                    </button>
                  </div>

                  {isTransactionsInitialLoading ? (
                    <div className="home__activity-loader" aria-label={home.loadingOperations}>
                      <span className="home__activity-loader-dot" />
                      <span className="home__activity-loader-dot" />
                      <span className="home__activity-loader-dot" />
                    </div>
                  ) : recentTransactions.length > 0 ? (
                    <TxCard
                      transactions={recentTransactions}
                      showCardContext
                      showFullDate
                      onSelect={(transaction) => navigate(`/history/${transaction.id}`, {
                        state: {
                          backgroundLocation: location,
                          skipRouteOverlayTransition: true,
                          transaction: getTransactionRouteSnapshot(transaction),
                        },
                      })}
                    />
                  ) : (
                    <div className="home__activity-empty">
                      <div className="home__activity-empty-icon" aria-hidden="true">
                        <ShootingStarIcon className="home__activity-empty-icon-svg" />
                      </div>
                      <p className="home__activity-empty-title">{home.noOperationsTitle}</p>
                      <p className="home__activity-empty-text">{home.noOperationsText}</p>
                    </div>
                  )}
                </section>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

    </section>
  );
}

export default Home;
